/**
 * Studio Payment Terms component
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign, Plus, X } from "lucide-react";
import { HIREABLE_ROLES, PAYMENT_TIMING_OPTIONS, RATE_TYPES, getLabel } from "@/lib/profileOptions";

export function StudioPaymentTerms({
  payRates, setPayRates,
  paymentTiming, setPaymentTiming
}) {
  const [newRole, setNewRole] = useState("second_photo");
  const [newRateType, setNewRateType] = useState("hourly");
  const [newFixedRate, setNewFixedRate] = useState("");
  const [newMinRate, setNewMinRate] = useState("");
  const [newMaxRate, setNewMaxRate] = useState("");
  const [useRange, setUseRange] = useState(false);

  const addPayRate = () => {
    if (!newRole) return;
    
    // Check if role already exists
    if (payRates.some(r => r.role === newRole)) {
      return;
    }

    const newPayRate = {
      role: newRole,
      rate_type: newRateType,
      fixed_rate: useRange ? null : (newFixedRate ? parseFloat(newFixedRate) : null),
      min_rate: useRange ? (newMinRate ? parseFloat(newMinRate) : null) : null,
      max_rate: useRange ? (newMaxRate ? parseFloat(newMaxRate) : null) : null
    };

    setPayRates([...payRates, newPayRate]);
    setNewFixedRate("");
    setNewMinRate("");
    setNewMaxRate("");
  };

  const removePayRate = (index) => {
    setPayRates(payRates.filter((_, i) => i !== index));
  };

  const formatRate = (rate) => {
    if (rate.fixed_rate) {
      return `$${rate.fixed_rate}`;
    }
    if (rate.min_rate && rate.max_rate) {
      return `$${rate.min_rate} - $${rate.max_rate}`;
    }
    if (rate.min_rate) {
      return `$${rate.min_rate}+`;
    }
    if (rate.max_rate) {
      return `Up to $${rate.max_rate}`;
    }
    return "Not set";
  };

  return (
    <>
      <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
        <h2 className="text-xl font-semibold mb-2">
          <DollarSign className="w-5 h-5 inline mr-2" />
          Pay Rates
        </h2>
        <p className="text-white/50 mb-6">What do you typically pay for each role?</p>

        {/* Existing pay rates */}
        {payRates.length > 0 && (
          <div className="space-y-3 mb-6">
            {payRates.map((rate, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-white/[0.05] border border-white/10 rounded-sm"
              >
                <div>
                  <span className="font-medium">{getLabel(HIREABLE_ROLES, rate.role)}</span>
                  <span className="text-white/50 mx-2">•</span>
                  <span className="text-white/70">{formatRate(rate)}</span>
                  <span className="text-white/50 ml-2 text-sm">({getLabel(RATE_TYPES, rate.rate_type)})</span>
                </div>
                <button
                  type="button"
                  onClick={() => removePayRate(index)}
                  className="text-white/40 hover:text-red-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new pay rate */}
        <div className="bg-white/[0.05] border border-white/10 rounded-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm text-white/60">Role</Label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="mt-2 w-full bg-[#0a0a0a] border border-white/10 text-white h-12 rounded-sm px-3"
              >
                {HIREABLE_ROLES.map((role) => (
                  <option key={role.value} value={role.value} disabled={payRates.some(r => r.role === role.value)}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm text-white/60">Rate Type</Label>
              <select
                value={newRateType}
                onChange={(e) => setNewRateType(e.target.value)}
                className="mt-2 w-full bg-[#0a0a0a] border border-white/10 text-white h-12 rounded-sm px-3"
              >
                {RATE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!useRange}
                onChange={() => setUseRange(false)}
                className="accent-white"
              />
              <span className="text-sm">Fixed Rate</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={useRange}
                onChange={() => setUseRange(true)}
                className="accent-white"
              />
              <span className="text-sm">Rate Range</span>
            </label>
          </div>

          {!useRange ? (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-sm text-white/60">Amount ($)</Label>
                <Input
                  type="number"
                  value={newFixedRate}
                  onChange={(e) => setNewFixedRate(e.target.value)}
                  placeholder="50"
                  className="mt-2 bg-[#0a0a0a] border-white/10 text-white h-12 rounded-sm"
                />
              </div>
              <Button
                type="button"
                onClick={addPayRate}
                disabled={!newFixedRate}
                className="mt-8 bg-white text-black hover:bg-gray-200 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-sm text-white/60">Min ($)</Label>
                <Input
                  type="number"
                  value={newMinRate}
                  onChange={(e) => setNewMinRate(e.target.value)}
                  placeholder="25"
                  className="mt-2 bg-[#0a0a0a] border-white/10 text-white h-12 rounded-sm"
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm text-white/60">Max ($)</Label>
                <Input
                  type="number"
                  value={newMaxRate}
                  onChange={(e) => setNewMaxRate(e.target.value)}
                  placeholder="50"
                  className="mt-2 bg-[#0a0a0a] border-white/10 text-white h-12 rounded-sm"
                />
              </div>
              <Button
                type="button"
                onClick={addPayRate}
                disabled={!newMinRate && !newMaxRate}
                className="mt-8 bg-white text-black hover:bg-gray-200 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
        <h2 className="text-xl font-semibold mb-6">Payment Timing</h2>
        <p className="text-white/50 mb-4">When do you typically pay your team?</p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {PAYMENT_TIMING_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={`p-4 bg-white/[0.05] border rounded-sm cursor-pointer transition-all text-center ${paymentTiming === option.value ? "border-white/40 bg-white/5" : "border-white/10"}`}
              onClick={() => setPaymentTiming(option.value)}
              data-testid={`payment-timing-${option.value}`}
            >
              <span className="font-medium">{option.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

export default StudioPaymentTerms;
